// Hijri birth dates are sent and stored as YYYY-MM-DD. Day 30 is valid in every Hijri month (e.g. 30 Safar).

export interface HijriBirthDateParts {
  year: string;
  /** 0-based index of the month option */
  monthIndex: number;
  /** without zero padding, to match the 1-30 day options */
  day: string;
}

const YEAR_FIRST_DATE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;
const MIN_HIJRI_YEAR = 1300;

/** 1300 to the current Hijri year, the range the API accepts (a stored Gregorian year such as 1985 is outside it). */
export function isHijriBirthYear(year: unknown): boolean {
  const y = String(year ?? '').trim();
  return /^\d{4}$/.test(y) && Number(y) >= MIN_HIJRI_YEAR && Number(y) <= currentHijriYear();
}

/** The Umm al-Qura year in Riyadh, as the API counts it; null when the runtime has no such calendar. */
export function hijriYearOn(date: Date): number | null {
  try {
    const format = new Intl.DateTimeFormat('en-US-u-ca-islamic-umalqura', {year: 'numeric', timeZone: 'Asia/Riyadh'});
    if (format.resolvedOptions().calendar !== 'islamic-umalqura') {
      return null;
    }
    const year = format.formatToParts(date).find(part => part.type === 'year');
    return year && /^\d{4}$/.test(year.value) ? Number(year.value) : null;
  } catch {
    return null;
  }
}

function currentHijriYear(): number {
  // without the calendar: a bound one year generous; the API still decides
  return hijriYearOn(new Date()) ?? Math.floor((new Date().getFullYear() - 622) * 33 / 32) + 1;
}

/** Splits a stored date (1405-03-07, 1405/3/7) for the form; null when it is empty, malformed or not a Hijri year, so nothing is pre-filled. */
export function parseHijriBirthDate(value: string | null | undefined): HijriBirthDateParts | null {
  const match = YEAR_FIRST_DATE.exec((value ?? '').trim());
  if (!match) {
    return null;
  }
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isHijriBirthYear(match[1]) || month < 1 || month > 12 || day < 1 || day > 30) {
    return null;
  }
  return {year: match[1], monthIndex: month - 1, day: String(day)};
}

/** Builds YYYY-MM-DD from the form's year, 0-based month option and day; null when a part is missing, not a number or not a Hijri year. */
export function composeHijriBirthDate(year: unknown, monthIndex: unknown, day: unknown): string | null {
  const y = String(year ?? '').trim();
  const m = toInteger(monthIndex) + 1;
  const d = toInteger(day);
  if (!isHijriBirthYear(y) || !(m >= 1 && m <= 12) || !(d >= 1 && d <= 30)) {
    return null;
  }
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function toInteger(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : NaN;
  }
  const text = String(value ?? '').trim();
  return /^\d{1,2}$/.test(text) ? Number(text) : NaN;
}
